import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { TID } from '@/constants/testIds';
import { Shield, Users as UsersIcon, BookOpen, Bot, Activity, Trash2, Sparkles } from 'lucide-react';
import { useLang } from '@/context/LangContext';
import BulkEnrichButton from '@/components/BulkEnrichButton';

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-card-steel border border-gold/30 rounded-lg p-4 hover-lift">
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5 text-gold" />
      </div>
      <div className="font-h text-3xl text-white">{value}</div>
      <div className="text-xs uppercase tracking-widest text-gray-400 mt-1">{label}</div>
    </div>
  );
}

export default function Admin() {
  const { t } = useLang();
  const [dash, setDash] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    api.get('/admin/dashboard').then(r => setDash(r.data));
    api.get('/admin/users').then(r => setUsers(r.data.users || []));
    api.get('/admin/logs').then(r => setActivity(r.data.activity || []));
  }, []);

  const removeUser = async (id) => {
    if (!window.confirm(t('admin_delete_user_confirm'))) return;
    await api.delete(`/admin/users/${id}`);
    setUsers(u => u.filter(x => x.id !== id));
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_admin')}</div>
        <h1 data-testid={TID.adminDashboard} className="font-h uppercase text-4xl sm:text-5xl flex items-center gap-3">
          <Shield className="w-9 h-9 text-gold" /> {t('admin_h1')}
        </h1>
      </div>

      <div className="flex gap-2 mb-6 border-b border-gold/20">
        {[
          { id: 'overview', key: 'admin_tab_overview' },
          { id: 'users', key: 'admin_tab_users' },
          { id: 'activity', key: 'admin_tab_activity' },
          { id: 'maintenance', key: 'admin_tab_maintenance' },
        ].map(tt => (
          <button
            key={tt.id}
            onClick={() => setTab(tt.id)}
            data-testid={`admin-tab-${tt.id}`}
            className={`px-4 py-2 font-h uppercase tracking-wider text-sm transition border-b-2 ${
              tab === tt.id ? 'border-gold text-gold' : 'border-transparent text-gray-400 hover:text-gold'
            }`}
          >
            {t(tt.key)}
          </button>
        ))}
      </div>

      {tab === 'overview' && dash && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat label={t('admin_coaches')} value={dash.stats.coaches} icon={UsersIcon} />
            <Stat label={t('admin_players')} value={dash.stats.players} icon={UsersIcon} />
            <Stat label={t('admin_plays_global')} value={dash.stats.plays_global} icon={BookOpen} />
            <Stat label={t('admin_ai_sessions')} value={dash.stats.ai_sessions} icon={Bot} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
              <h3 className="font-h uppercase text-lg mb-3 text-gold">{t('admin_top_plays')}</h3>
              <div className="space-y-2">
                {(dash.top_plays || []).map(p => (
                  <div key={p.id} className="flex items-center justify-between border-b border-steel py-2 last:border-b-0">
                    <div>
                      <div className="font-h uppercase text-white">{p.name}</div>
                      <div className="text-xs text-gray-400 uppercase">{p.category}</div>
                    </div>
                    <div className="text-gold font-mono">{Math.round(p.success_rate * 100)}%</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
              <h3 className="font-h uppercase text-lg mb-3 text-gold">{t('admin_by_category')}</h3>
              <div className="space-y-2.5">
                {Object.entries(dash.by_category || {}).map(([k, v]) => (
                  <div key={k}>
                    <div className="flex items-center justify-between text-xs uppercase tracking-wider mb-1">
                      <span className="text-gray-300">{k}</span>
                      <span className="text-gold font-mono">{v}</span>
                    </div>
                    <div className="h-1.5 bg-navy-deep rounded">
                      <div className="h-full bg-gold rounded" style={{ width: `${Math.min(100, (v / 30) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'users' && (
        <div className="bg-card-steel border border-gold/30 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-navy-deep text-gold text-xs uppercase tracking-widest">
                <th className="text-start p-3">{t('admin_col_name')}</th>
                <th className="text-start p-3">{t('admin_col_email')}</th>
                <th className="text-start p-3">{t('admin_col_role')}</th>
                <th className="text-start p-3">{t('admin_col_created')}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-steel hover:bg-navy-deep/50">
                  <td className="p-3">{u.name}</td>
                  <td className="p-3 font-mono text-sm text-gray-300">{u.email}</td>
                  <td className="p-3"><span className="text-xs px-2 py-0.5 rounded border border-gold/40 text-gold uppercase">{u.role}</span></td>
                  <td className="p-3 text-xs text-gray-400">{u.created_at?.slice(0, 10)}</td>
                  <td className="p-3 text-end">
                    {u.role !== 'admin' && (
                      <button onClick={() => removeUser(u.id)} className="p-1.5 rounded border border-alert/50 text-alert hover:bg-alert/10" data-testid={`admin-delete-user-${u.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'activity' && (
        <div className="bg-card-steel border border-gold/30 rounded-lg p-5">
          <h3 className="font-h uppercase text-lg mb-3 text-gold flex items-center gap-2"><Activity className="w-4 h-4" /> {t('admin_recent_activity')}</h3>
          <div className="space-y-2">
            {activity.map((a, i) => (
              <div key={i} className="border-b border-steel py-2 last:border-b-0 text-sm flex items-center justify-between">
                <div>
                  <span className="font-mono text-gold text-xs uppercase tracking-wider me-2">{a.type}</span>
                  <span className="text-gray-200">{a.snippet || a.name}</span>
                </div>
                <div className="text-xs text-gray-500">{a.at?.slice(0, 16).replace('T', ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'maintenance' && (
        <div className="max-w-2xl bg-card-steel border-2 border-gold/40 rounded-lg p-6" data-testid="admin-maintenance">
          <h3 className="font-h uppercase text-xl mb-2 text-gold flex items-center gap-2">
            <Sparkles className="w-5 h-5" /> {t('admin_backfill_title')}
          </h3>
          <p className="text-xs text-gray-300 leading-relaxed mb-4">
            {t('admin_backfill_body')}
          </p>
          <BulkEnrichButton />
        </div>
      )}
    </Layout>
  );
}
